import {
    areFieldsSet,
    logger,
    parseAspDate,
} from '@superbet-group/web.lib.utilities';
import {
    CustomContentDto,
    CustomContentSection,
    CustomContentType,
} from '@app/promotionHub/shared/types/CustomContent';

import {
    Promotion,
    PromotionBonusType,
    PromotionStatus,
} from '@app/promotionHub/promotions/types/Promotion';
import {
    ComtradePromotionDto,
    PromotionBonusStatusDto,
    PromotionStatusDto,
} from '@src/app/promotionHub/promotions/types/dtos/ComtradePromotionDto';
import { transformConditions } from '@app/promotionHub/shared/mappers/transformConditions';
import { BonusKind } from '@core/models/bonus/Bonus';
import { isPromotionWelcomeOffer } from '@store/modules/ui/user/promotions/helpers';
import { getTermsAndConditionsUrl } from '../utils/helpers';

export function transformComtradePromotion(dto: ComtradePromotionDto): Promotion {
    return {
        kind: BonusKind.Comtrade,
        promotionId: dto.PromotionId,
        title: dto.PromotionFriendlyName || dto.PromotionName,
        description: dto.Description,
        type: dto.PromotionType,
        status: getPromotionStatus(dto),
        expirationDate: parseAspDate(dto.PromotionEndDate),
        image: getPromotionImage(dto.CustomContentList, dto.BonusType),
        conditions: transformConditions(dto.CustomContentList, CustomContentSection.Promotion, dto),
        bonus: {
            name: dto.BonusName,
            conditions: transformConditions(dto.CustomContentList, CustomContentSection.PromotionBonus, dto),
            type: dto.BonusType,
            expirationDate: dto.BonusExpirationDate ? parseAspDate(dto.BonusExpirationDate) : undefined,
        },

        ...areFieldsSet(dto, 'OptInCode') && { optInCode: dto.OptInCode },
        ...areFieldsSet(dto, 'CustomContentList') && { bonusSummaryTitle: getCustomContentValue(dto.CustomContentList, CustomContentType.promoBonusTitle) },
        ...areFieldsSet(dto, 'CustomContentList') && { bonusValue: getCustomContentValue(dto.CustomContentList, CustomContentType.promoBonusValueString) },
        ...areFieldsSet(dto, 'CustomContentList') && {
            promoActionButton: {
                text: getCustomContentValue(dto.CustomContentList, CustomContentType.promoButtonCtaText),
                link: getCustomContentValue(dto.CustomContentList, CustomContentType.promoButtonCtaUrl),
            },
        },
        ...areFieldsSet(dto, 'CustomContentList') && { termsAndConditionsUrl: getTermsAndConditionsUrl(getCustomContentValue(dto.CustomContentList, CustomContentType.promoTerms)) },
    };
}

function getPromotionStatus(promotionDto: ComtradePromotionDto): PromotionStatus {
    if (promotionDto.PromotionStatus === null) {
        return promotionDto.BonusExpirationDate && parseAspDate(promotionDto.BonusExpirationDate).getTime() >= (new Date().getTime())
            ? PromotionStatus.Available
            : PromotionStatus.Unspecified;
    // WelcomeOffer is a special case, Active bonus state means that user got only one of multiple bonuses
    } else if (promotionDto.PromotionStatus === PromotionStatusDto.OptedIn
        && promotionDto.ICoreAggregatedBonusStatus === PromotionBonusStatusDto.Active
        && isPromotionWelcomeOffer(promotionDto.OptInCode)) {
        return PromotionStatus.InProgress;
    // Only Promotions with status OptedIn and that have expired should be in Expired category
    } else if (promotionDto.BonusExpirationDate && parseAspDate(promotionDto.BonusExpirationDate).getTime() < (new Date().getTime())
        && promotionDto.PromotionStatus === PromotionStatusDto.OptedIn) {
        return PromotionStatus.Expired;
    } else {
        const status = promotionStatusMappings[promotionDto.PromotionStatus];
        if (!status) {
            logger.error(new Error(`Undefined category for bonus status: ${promotionDto.PromotionStatus}`));
        }
        return typeof status === 'object' ? status[promotionDto.ICoreAggregatedBonusStatus] : status;
    }
}

function getCustomContentValue(contentList: CustomContentDto[], contentKey: CustomContentType): string | undefined {
    return contentList.find((item) => item.Type === contentKey)?.Content;
}

function getPromotionImage(customContentList: CustomContentDto[] | undefined, bonusType: PromotionBonusType | null): string {
    const image = customContentList?.find((content) => content.Type === CustomContentType.promoImage)?.Content || null;
    return image ?? (bonusType ? imageMapping[bonusType] : '');
}

const imageMapping: Record<PromotionBonusType, string> = {
    [PromotionBonusType.Sportsbook]: '/static/img/promotions/sport.png',
    [PromotionBonusType.FreeBet]: '/static/img/promotions/sport.png',
    [PromotionBonusType.Casino]: '/static/img/promotions/casino.png',
    [PromotionBonusType.External]: '/static/img/promotions/casino.png',
    [PromotionBonusType.Honey]: '/static/img/promotions/casino.png',
    [PromotionBonusType.WelcomeOffer]: '/static/img/promotions/welcome-offer.png',
};

const promotionStatusMappings: Record<PromotionStatusDto, PromotionStatus | Record<PromotionBonusStatusDto, PromotionStatus>> = {
    [PromotionStatusDto.OptedOut]: PromotionStatus.History,
    [PromotionStatusDto.OptedIn]: {
        [PromotionBonusStatusDto.Active]: PromotionStatus.Completed,
        [PromotionBonusStatusDto.Success]: PromotionStatus.Completed,
        [PromotionBonusStatusDto.Failed]: PromotionStatus.Completed,
        [PromotionBonusStatusDto.Other]: PromotionStatus.History,
        [PromotionBonusStatusDto.Available]: PromotionStatus.InProgress,
    },
    [PromotionStatusDto.NoOptInRequired]: {
        [PromotionBonusStatusDto.Active]: PromotionStatus.Completed,
        [PromotionBonusStatusDto.Success]: PromotionStatus.Completed,
        [PromotionBonusStatusDto.Failed]: PromotionStatus.Completed,
        [PromotionBonusStatusDto.Other]: PromotionStatus.History,
        [PromotionBonusStatusDto.Available]: PromotionStatus.InProgress,
    },
};
